import React, { useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon, Download as DownloadIcon } from '@mui/icons-material';
import { type Bill } from '../hooks/useBillList';
import jsPDF from 'jspdf';

interface BillReceiptDialogProps {
  open: boolean;
  onClose: () => void;
  bill: Bill | null;
}

// Function to convert number to words in Indian format
const numberToWords = (num: number): string => {
  const ones = [
    '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
    'Seventeen', 'Eighteen', 'Nineteen'
  ];
  
  const tens = [
    '', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'
  ];
  
  const convertHundreds = (n: number): string => {
    let result = '';
    
    if (n >= 100) {
      result += ones[Math.floor(n / 100)] + ' Hundred ';
      n %= 100;
    }
    
    if (n >= 20) {
      result += tens[Math.floor(n / 10)] + ' ';
      n %= 10;
    }
    
    if (n > 0) {
      result += ones[n] + ' ';
    }
    
    return result.trim();
  };
  
  if (num === 0) return 'Zero';
  
  let words = '';
  const crores = Math.floor(num / 10000000);
  const lakhs = Math.floor((num % 10000000) / 100000);
  const thousands = Math.floor((num % 100000) / 1000);
  const hundreds = num % 1000;
  
  if (crores > 0) {
    words += convertHundreds(crores) + ' Crore ';
  }
  
  if (lakhs > 0) {
    words += convertHundreds(lakhs) + ' Lakh ';
  }
  
  if (thousands > 0) {
    words += convertHundreds(thousands) + ' Thousand ';
  }
  
  if (hundreds > 0) {
    words += convertHundreds(hundreds);
  }
  
  return words.trim() + ' Rupees Only';
};

const BillReceiptDialog: React.FC<BillReceiptDialogProps> = ({ open, onClose, bill }) => {
  const receiptRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadPDF = async () => {
    if (!bill || !receiptRef.current) return;

    setIsDownloading(true);
    try {
      // Wait for images to load and get their dimensions
      const images = receiptRef.current.querySelectorAll('img');
      let svgWidth = 816; // Default SVG width from viewBox
      let svgHeight = 528; // Default SVG height from viewBox

      await Promise.all(
        Array.from(images).map((img) => {
          return new Promise((resolve) => {
            if (img.complete && img.naturalWidth > 0) {
              svgWidth = img.naturalWidth;
              svgHeight = img.naturalHeight;
              resolve(true);
            } else {
              img.onload = () => {
                svgWidth = img.naturalWidth || 816;
                svgHeight = img.naturalHeight || 528;
                resolve(true);
              };
              img.onerror = () => resolve(true);
            }
          });
        })
      );

      // Set the container size based on SVG dimensions
      receiptRef.current.style.width = `${svgWidth}px`;
      receiptRef.current.style.height = `${svgHeight}px`;

      // Additional wait to ensure everything is rendered, especially SVG
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Import html2canvas dynamically
      const html2canvas = (await import('html2canvas')).default;

      // Use optimized scale (4-5) for high quality with reasonable file size
      const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
      // Scale of 4-5 provides ~384-480 DPI equivalent - excellent quality for logos
      const scale = Math.min(Math.max(dpr * 4, 4), 5);

      const canvas = await html2canvas(receiptRef.current, {
        backgroundColor: '#ffffff',
        scale: scale,
        useCORS: true,
        allowTaint: true,
        logging: false,
        removeContainer: false,
        imageTimeout: 25000,
        onclone: (clonedDoc) => {
          // Optimize rendering in cloned document for maximum quality
          const body = clonedDoc.body;
          if (body && body instanceof HTMLElement) {
            body.style.setProperty('-webkit-font-smoothing', 'antialiased');
            body.style.setProperty('text-rendering', 'optimizeLegibility');
            // Use 'crisp-edges' for image-rendering to ensure sharp logos and text
            body.style.setProperty('image-rendering', 'crisp-edges');
          }
          // Optimize all text elements
          const textElements = clonedDoc.querySelectorAll('*');
          textElements.forEach((el) => {
            if (el instanceof HTMLElement) {
              el.style.setProperty('-webkit-font-smoothing', 'antialiased');
              el.style.setProperty('text-rendering', 'optimizeLegibility');
              el.style.setProperty('font-smooth', 'always');
            }
          });
          // Ensure SVG images are rendered at maximum quality
          const images = clonedDoc.querySelectorAll('img');
          images.forEach((img) => {
            if (img instanceof HTMLImageElement) {
              // Use crisp-edges for sharper logo rendering
              img.style.setProperty('image-rendering', 'crisp-edges');
              // Force reload at full resolution
              if (img.src && img.src.endsWith('.svg')) {
                img.style.width = 'auto';
                img.style.height = 'auto';
                img.style.maxWidth = 'none';
                img.style.maxHeight = 'none';
              }
            }
          });
        },
        foreignObjectRendering: false,
      });

      // Validate canvas
      if (!canvas || canvas.width === 0 || canvas.height === 0) {
        throw new Error('Canvas is empty or invalid');
      }

      // Original dimensions for PDF (816x528)
      const pdfWidth = svgWidth;
      const pdfHeight = svgHeight;

      // Use JPEG with maximum quality (0.99) for highest quality while maintaining reasonable file size
      // At scale 4-5, JPEG 0.99 provides near-lossless quality with excellent compression
      const imgData = canvas.toDataURL('image/jpeg', 0.99);

      // Validate image data
      if (!imgData || imgData === 'data:,') {
        throw new Error('Failed to generate image data');
      }

      // Create PDF with original dimensions but embed high-resolution image
      const pdf = new jsPDF({
        orientation: pdfWidth > pdfHeight ? 'landscape' : 'portrait',
        unit: 'px',
        format: [pdfWidth, pdfHeight],
        compress: false,
      });

      // Add high-resolution image scaled to PDF dimensions
      // JPEG format with 0.99 quality provides maximum visual quality with excellent compression
      pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);

      const fileName = `bill-receipt-${bill.registrationNo}.pdf`;
      pdf.save(fileName);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  if (!bill) return null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return '#10b981';
      case 'overdue': return '#ef4444';
      case 'pending': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e293b' }}>
            Bill Receipt
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <Box
              sx={{
                backgroundColor: getStatusColor(bill.status),
                color: 'white',
                px: 2,
                py: 0.5,
                borderRadius: 2,
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'uppercase',
              }}
            >
              {bill.status}
            </Box>
          </Box>

          <Divider />

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#1e293b' }}>
                Student Information
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Name:</strong> {bill.studentName}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Registration No:</strong> {bill.registrationNo}
              </Typography>
              <Typography variant="body2">
                <strong>Course:</strong> {bill.course}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#1e293b' }}>
                Payment Information
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Bill Number:</strong> {bill.billNumber || bill.id || bill._id || '-'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Amount:</strong> ₹{bill.amount.toLocaleString()}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Payment Method:</strong> {bill.paymentMethod}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Bill Date:</strong> {new Date(bill.billDate).toLocaleDateString()}
              </Typography>
              <Typography variant="body2">
                <strong>Due Date:</strong> {new Date(bill.dueDate).toLocaleDateString()}
              </Typography>
            </Box>
          </Box>

          {bill.description && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#1e293b' }}>
                  Description
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  {bill.description}
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={onClose}
          sx={{
            color: '#64748b',
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
            py: 1,
            borderRadius: 2,
          }}
        >
          Close
        </Button>
        <Button
          onClick={handleDownloadPDF}
          variant="contained"
          startIcon={<DownloadIcon />}
          disabled={isDownloading}
          sx={{
            backgroundColor: '#3b82f6',
            color: 'white',
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
            py: 1,
            borderRadius: 2,
            '&:hover': {
              backgroundColor: '#2563eb',
            },
          }}
        >
          {isDownloading ? 'Generating PDF...' : 'Download PDF'}
        </Button>
      </DialogActions>

      {/* Hidden receipt template for PDF generation */}
      <Box
        ref={receiptRef}
        sx={{
          position: 'absolute',
          left: '-9999px',
          top: '-9999px',
          backgroundColor: '#ffffff',
          overflow: 'visible',
          width: '816px',
          height: '528px',
        }}
      >
        {/* SVG Template Background */}
        <Box
          component="img"
          src="/studentReceiptTemplate.svg"
          alt="Receipt Template"
          sx={{
            display: 'block',
            zIndex: 0,
            width: '816px',
            height: '528px',
            imageRendering: 'auto',
            objectFit: 'fill',
          }}
        />
        
        {/* Overlay Text Values */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 1,
            fontFamily: 'Arial, sans-serif',
          }}
        >
          {/* Receipt Number (No. :) - Left side, below logo/accreditation */}
          <Typography
            sx={{
              position: 'absolute',
              left: '110px',
              top: '211px',
              fontSize: '13px',
              fontWeight: 'bold',
              color: '#000000',
            }}
          >
            {bill.billNumber || bill.id || bill._id || ''}
          </Typography>

          {/* Student Name - Above receipt number */}
          {/* <Typography
            sx={{
              position: 'absolute',
              left: '120px',
              top: '175px',
              fontSize: '14px',
              fontWeight: 'bold',
              color: '#000000',
            }}
          >
            {bill.studentName}
          </Typography> */}

          {/* Course Name - Below receipt number */}
          {/* <Typography
            sx={{
              position: 'absolute',
              left: '110px',
              top: '235px',
              fontSize: '13px',
              fontWeight: 'bold',
              color: '#000000',
            }}
          >
            {bill.course}
          </Typography> */}

          {/* Bill Date (Dated :) - Upper right */}
          <Typography
            sx={{
              position: 'absolute',
              left: '615px',
              top: '203px',
              fontSize: '15px',
              color: '#000000',
              fontWeight: 'bold',
            }}
          >
            {new Date(bill.billDate).toLocaleDateString('en-GB')}
          </Typography>

          {/* Received with thank from Sh./Smt. : [Student Name] */}
          <Typography
            sx={{
              position: 'absolute',
              left: '333px',
              top: '244px',
              fontSize: '1rem',
              color: '#000000',
              fontWeight: 'bold'
            }}
          >
            {bill.studentName}
          </Typography>

          {/* Amount in words - "the sum of Rupees:" followed by amount in words */}
          <Typography
            sx={{
              position: 'absolute',
              left: '220px',
              top: '317.5px',
              fontSize: '1rem',
              fontWeight: 'bold',
              color: '#000000',
              maxWidth: '250px',
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              whiteSpace: 'normal',
              lineHeight: '1.4',
            }}
          >
            {numberToWords(bill.amount)}
          </Typography>

          {/* Description (on account of :) */}
          {/* {bill.description && (
            <Typography
              sx={{
                position: 'absolute',
                left: '90px',
                top: '350px',
                fontSize: '13px',
                fontWeight: 'bold',
                color: '#000000',
                maxWidth: '400px',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                whiteSpace: 'normal',
                lineHeight: '1.4',
              }}
            >
              {bill.description}
            </Typography>
          )} */}

          {/* Payment Method (by Cash/Cheque/Draft No. :) */}
          <Typography
            sx={{
              position: 'absolute',
              left: '290px',
              top: '392px',
              fontSize: '0.9rem',
              fontWeight: 'bold',
              color: '#000000',
              textTransform: 'capitalize',
            }}
          >
            {bill.paymentMethod.replace('_', ' ')}
          </Typography>

          {/* Secondary Date (Dated :) - Payment date or due date */}
          <Typography
            sx={{
              position: 'absolute',
              left: '615px',
              top: '393px',
              fontSize: '15px',
              color: '#000000',
              fontWeight: 'bold',
            }}
          >
            {new Date(bill.billDate).toLocaleDateString('en-GB')}
          </Typography>

          {/* Numerical Amount - ₹{amount} */}
          <Typography
            sx={{
              position: 'absolute',
              left: '160px',
              top: '438px',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              color: '#000000',
            }}
          >
            {bill.amount.toLocaleString()}
          </Typography>
        </Box>
      </Box>
    </Dialog>
  );
};

export default BillReceiptDialog;
